'use client';

import { JSX, KeyboardEventHandler, useEffect } from 'react';

import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { sendGTMFormErrorEvent } from '@/components/helpers/analytics/gtm';
import { IAtCheckboxFieldProps } from './at-checkbox-field.types';

/**
 * `AtCheckboxField` is a fully accessible, React Hook Form–compatible checkbox component
 * used within controlled forms. It handles validation, keyboard interaction, and
 * automatically triggers GTM form error events when validation fails.
 *
 * This component is intended for **form use only**. For UI-driven checkboxes (e.g., in filter UIs),
 * use `AtCheckbox` instead.
 */
export const AtCheckboxField = ({
  id,
  className,
  required,
  children,
  ...props
}: IAtCheckboxFieldProps): JSX.Element => {
  const {
    register,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const hasError = Boolean(errors[id]);
  const errorMessage = hasError ? (errors[id]?.message as string) : '';
  const checked = !!watch(id);

  useEffect(() => {
    if (hasError) {
      sendGTMFormErrorEvent({
        form_name: getValues('formName'),
        form_field: id,
        error_message: errorMessage,
      });
    }
  }, [errorMessage, getValues, hasError, id]);

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
    if (props.disabled || isSubmitting) {
      return;
    }
    if (e.key === 'Enter') {
      setValue(id, !checked);
    }
  };

  const handleClick = () => {
    if (props.disabled || isSubmitting) {
      return;
    }
    setValue(id, !checked);
  };

  return (
    <div className={classNames('relative flex w-full flex-col')}>
      <label
        className="paragraph-md flex cursor-pointer items-center gap-2 text-skin-secondary"
        id={`label-${id}`}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
      >
        <span
          role="checkbox"
          tabIndex={0}
          aria-checked={checked}
          aria-labelledby={`label-${id}`}
          {...register(id, {
            required,
            disabled: props.disabled || isSubmitting,
          })}
          aria-required={required}
          {...(hasError
            ? {
                'aria-invalid': hasError,
                'aria-describedby': `errorMessage-${id}`,
              }
            : {})}
          id={id}
          className={classNames(
            'relative h-7 w-7 flex-shrink-0 rounded-full border-2 transition-all duration-150',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2',
            className,
            {
              'border-skin-stroke-primary peer-focus-visible:ring-checkbox-checked':
                checked,
              'border-neutral-300 peer-focus-visible:ring-checkbox-unchecked':
                !checked,
            },
          )}
        >
          {checked && (
            <span className="absolute left-1/2 top-1/2 h-4.5 w-4.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-skin-ui-fill-primary" />
          )}
        </span>
        {children}
      </label>
      {hasError && (
        <p
          id={`errorMessage-${id}`}
          className="mt-2 text-xs text-skin-alert"
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
